import {
  Component,
  OnInit,
  Injectable,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { LoadScripts, DynamicScript } from 'src/app/shared/utilities/load-scripts.function';
import { delay } from 'rxjs/operators';
import { Playable } from 'src/app/shared/directives/playable.directive';

interface FbVideoInstance {
  play: () => void;
  pause: () => void;
  mute: () => void;
  unmute: () => void;
}

interface FbVideoReadyMessage {
  type: 'video';
  instance: FbVideoInstance;
}

interface FacebookSDK {
  XFBML: {
    parse: (element?: HTMLElement) => void;
  };
  Event: {
    subscribe: (eventName: string, callback: (msg: FbVideoReadyMessage) => void) => void;
  };
}

declare var FB: FacebookSDK;

@Injectable({ providedIn: 'root' })
export class FacebookService {
  static behaviourSubject: BehaviorSubject<any>;

  constructor() {}

  addVideo(): Observable<any> {
    if (FacebookService.behaviourSubject == null) {
      FacebookService.behaviourSubject = new BehaviorSubject<any>('initializing');

      let srcProtocal = 'https:' == document.location.protocol ? 'https:' : 'http:';
      let scriptUrl = srcProtocal + '//connect.facebook.net/en_US/sdk.js#xfbml=1&version=v17.0';

      LoadScripts([{ src: scriptUrl }] as DynamicScript[]).then(() => {
        FacebookService.behaviourSubject.next('loaded');
      });
    }

    return FacebookService.behaviourSubject.asObservable();
  }
}

@Component({
  selector: 'fgb-facebook-player',
  templateUrl: './fgb-facebook-player.component.html',
  styleUrls: ['./fgb-facebook-player.component.scss'],
})
export class FgbFacebookPlayerComponent extends Playable implements OnInit, OnDestroy {
  @Input() streamKey: string;
  @Output() onStateChange = new EventEmitter();
  @ViewChild('videoWrapper', { static: true }) videoWrapper!: ElementRef<HTMLDivElement>;

  private subscription: Subscription;
  public static analyticsInterval = 0;
  private sdkLoaded = false;
  private facebookService = inject(FacebookService);

  ngOnInit() {
    this.subscription = this.facebookService
      .addVideo()
      .pipe(delay(100))
      .subscribe((facebookService) => {
        if (facebookService == 'loaded') {
          this.sdkLoaded = true;
          this.onReady.emit(); // call onReady to tell media player is ready and can now be played
        }
      });
  }

  play() {
    if (!this.sdkLoaded) return;

    const wrapper = this.videoWrapper.nativeElement;

    wrapper.innerHTML = '';
    const fbVideoDiv = document.createElement('div');
    fbVideoDiv.className = 'fb-video';
    fbVideoDiv.setAttribute('data-href', this.streamKey);
    fbVideoDiv.setAttribute('data-width', 'auto');
    fbVideoDiv.setAttribute('data-height', '528');
    fbVideoDiv.setAttribute('data-allowfullscreen', 'true');

    wrapper.appendChild(fbVideoDiv);

    if (typeof FB !== 'undefined') {
      FB.XFBML.parse(wrapper);

      FB.Event.subscribe('xfbml.ready', (msg: any) => {
        if (msg.type === 'video') {
          msg.instance.mute();
          msg.instance.play();
          this.onStateChange.emit('playing');
        }
      });
    }
  }

  public pause(): void {}

  public stop(): void {}

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
